import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Skeleton,
  Alert,
  Button,
  Divider,
  Space,
  Statistic,
  Row,
  Col,
  Table,
  Empty,
  Descriptions,
  Tag,
} from 'antd';
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  BarChartOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import { Notice } from '../../components/common/Notice';
import analyticsService from '../../services/analytics.service';
import { AnalyticsData, isQualitativeAnalytics } from '../../types/analytics.types';
import { parseVisibilityDeniedError } from '../../services/surveyAnalysis.service';

// Phase 6 - US4: Survey Analytics for Completed Surveys (T113)

const ParticipantSurveyAnalytics = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (surveyId) {
      loadAnalytics();
    }
  }, [surveyId]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await analyticsService.getSurveyAnalytics(surveyId!);
      setAnalytics(data);
    } catch (err: any) {
      const denied = parseVisibilityDeniedError(err);
      if (denied) {
        setError(`Acesso aos resultados bloqueado: ${denied.message}`);
      } else {
        setError(
          err.response?.data?.error ||
          'Erro ao carregar indicadores da pesquisa'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          Voltar
        </Button>
        <Alert
          message="Erro"
          description={error || 'Indicadores não disponíveis'}
          type="error"
          showIcon
        />
      </div>
    );
  }

  const getQuestionSummary = () => {
    if (!analytics || !isQualitativeAnalytics(analytics) || !analytics.responseDistribution) return [];

    return analytics.responseDistribution.map((q) => ({
      id: q.questionId,
      shortDescription: q.questionText,
      type: q.questionType,
      totalResponses: q.totalResponses || 0,
      responseRate:
        q.totalResponses && analytics.totalResponses
          ? (
            ((q.totalResponses / analytics.totalResponses) * 100).toFixed(1)
          ).toString() + '%'
          : '-',
    }));
  };

  const questionColumns = [
    {
      title: 'Pergunta',
      dataIndex: 'shortDescription',
      key: 'shortDescription',
      width: '50%',
    },
    {
      title: 'Tipo',
      dataIndex: 'type',
      key: 'type',
      width: '20%',
      render: (type: string) => <Tag>{type}</Tag>,
    },
    {
      title: 'Respostas',
      dataIndex: 'totalResponses',
      key: 'totalResponses',
      width: '15%',
      align: 'center' as const,
    },
    {
      title: 'Taxa',
      dataIndex: 'responseRate',
      key: 'responseRate',
      width: '15%',
      align: 'center' as const,
    },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/surveys')}
          className="mb-4"
        >
          Voltar para Pesquisas
        </Button>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Indicadores da Pesquisa
        </h1>
        <p className="text-gray-600">
          Visualize os resultados e análise da pesquisa finalizada
        </p>
      </div>

      {/* Key Metrics */}
      <Card className="mb-6">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Total de Respostas"
              value={analytics.totalResponses || 0}
              prefix={<BarChartOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Total de Perguntas"
              value={isQualitativeAnalytics(analytics) ? analytics.responseDistribution?.length || 0 : 0}
            />
          </Col>
          {analytics.collectionPeriod && (
            <>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="Data Inicial"
                  value={new Date(analytics.collectionPeriod.startDate).toLocaleDateString(
                    'pt-BR'
                  )}
                  prefix={<CalendarOutlined />}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="Data Final"
                  value={new Date(analytics.collectionPeriod.endDate).toLocaleDateString(
                    'pt-BR'
                  )}
                  prefix={<CalendarOutlined />}
                />
              </Col>
            </>
          )}
        </Row>
      </Card>

      <Divider />

      {/* Survey Info */}
      <Card title="Informações da Pesquisa" className="mb-6">
        <Descriptions column={1}>
          <Descriptions.Item label="Nome da Pesquisa">
            {analytics.surveyName}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color="blue">{analytics.status}</Tag>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Divider />

      {/* Questions Overview */}
      <Card
        title={`Resumo das Perguntas (${isQualitativeAnalytics(analytics) ? analytics.responseDistribution?.length || 0 : 0})`}
        className="mb-6"
      >
        {!isQualitativeAnalytics(analytics) || !analytics.responseDistribution || analytics.responseDistribution.length === 0 ? (
          <Empty description="Nenhuma pergunta encontrada" />
        ) : (
          <Table
            columns={questionColumns}
            dataSource={getQuestionSummary()}
            rowKey="id"
            pagination={false}
            size="small"
          />
        )}
      </Card>

      {/* Detailed Analytics */}
      {isQualitativeAnalytics(analytics) && analytics.responseDistribution &&
        analytics.responseDistribution.map((question) => (
          <Card
            key={question.questionId}
            title={question.questionText}
            className="mb-6"
          >
            <div className="space-y-4">
              <div>
                <p><strong>Tipo:</strong> {question.questionType}</p>
                <p><strong>Total de Respostas:</strong> {question.totalResponses}</p>
              </div>
              {question.distribution && Object.keys(question.distribution).length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Distribuição de Respostas</h4>
                  <Table
                    columns={[
                      {
                        title: 'Opção',
                        dataIndex: 'choice',
                        key: 'choice',
                      },
                      {
                        title: 'Quantidade',
                        dataIndex: 'count',
                        key: 'count',
                        align: 'center' as const,
                      },
                      {
                        title: 'Percentual',
                        key: 'percentage',
                        align: 'center' as const,
                        render: (_: any, record: any) =>
                          (
                            ((record.count / (analytics.totalResponses || 1)) *
                              100).toFixed(1)
                          ).toString() + '%',
                      },
                    ]}
                    dataSource={Object.entries(question.distribution)
                      .map(([choice, count]) => ({
                        choice,
                        count: count as number,
                      }))}
                    rowKey="choice"
                    pagination={false}
                    size="small"
                  />
                </div>
              )}
            </div>
          </Card>
        ))}

      {/* Actions */}
      <Space className="w-full flex justify-center py-6">
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/surveys')}
        >
          Voltar
        </Button>
        <Button
          type="primary"
          icon={<HomeOutlined />}
          onClick={() => navigate('/')}
        >
          Ir para Home
        </Button>
      </Space>

      {/* Info Footer */}
      <Notice
        message="Informação"
        description="Os indicadores são atualizados em tempo real conforme novas respostas são recebidas. Para pesquisas em progresso, apenas a contagem de respostas é exibida. Análises detalhadas estão disponíveis após o término da pesquisa."
        type="info"
        showIcon
        className="mt-6"
      />
    </div>
  );
};

export default ParticipantSurveyAnalytics;
